/**
 * Clients API Hook
 *
 * Hook for fetching client data from the authenticated API.
 */

import { useApiData, useAuthenticatedFetch } from './client';
import type { Client } from './types';

/**
 * Hook for fetching the list of clients.
 *
 * Automatically fetches clients on mount and handles authentication.
 *
 * @param options - Optional query parameters
 * @returns Client list data, loading state, error, and refetch function
 */
export function useClients(options?: {
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
  /** Cache-buster: change to force a refetch (backend ignores it). */
  refreshKey?: number;
}) {
  // Build query string from options
  // NOTE: api-edna accepts `limit`, `order_by`, and `ascending` only. It has no
  // `skip`/`offset`/cursor, and no eager-loading flags — collections are capped at
  // 1000 with truncation signalled in the `X-Result-Truncated` response header.
  // Options it ignores were removed rather than left as silent no-ops.
  const params = new URLSearchParams();
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.orderBy) params.set('order_by', options.orderBy);
  if (options?.ascending !== undefined) params.set('ascending', String(options.ascending));
  if (options?.refreshKey) params.set('_r', String(options.refreshKey));

  const queryString = params.toString();
  const endpoint = queryString ? `/clients?${queryString}` : '/clients';

  return useApiData<Client[]>(endpoint);
}

/**
 * Hook for fetching a single client by ID.
 *
 * @param clientId - The client ID to fetch
 * @returns Client data, loading state, error, and refetch function
 */
export function useClient(clientId: number) {
  return useApiData<Client>(`/clients/${clientId}`);
}

/**
 * Hook for client mutations (create, update, delete).
 */
export function useClientMutations() {
  const { fetchData } = useAuthenticatedFetch<Client>();

  const createClient = (data: { name: string; clientcode: string }) =>
    fetchData('/clients', {
      method: 'POST',
      body: JSON.stringify({ ...data, creator: '', ts: new Date().toISOString() }),
    });

  const updateClient = (id: number, data: { name?: string; clientcode?: string }) =>
    fetchData(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

  const deleteClient = (id: number) =>
    fetchData(`/clients/${id}`, { method: 'DELETE' });

  return { createClient, updateClient, deleteClient };
}
